import type { StoryDetailsCommon } from "./dataStoryFlow.types";

/** The subset of a details form the audience adder touches. */
type AudiencePatch = Pick<
  StoryDetailsCommon,
  "audiences" | "audienceDraft" | "isAddingAudience"
>;

/**
 * Patch that commits whatever is in the adder as a chip and closes it.
 *
 * A duplicate is dropped rather than appended: chips are keyed by their own
 * string, so a repeat would collide on key as well as read as a bug. Blank
 * input closes the adder without adding anything.
 *
 * Extracted here because both details steps (blank-canvas and AI) draw the same
 * adder — this was copy-pasted logic in the blank step before the AI frame
 * needed it.
 */
export const commitAudiencePatch = (
  values: StoryDetailsCommon,
): AudiencePatch => {
  const audience = values.audienceDraft.trim();
  const isNew = audience !== "" && !values.audiences.includes(audience);

  return {
    audiences: isNew ? [...values.audiences, audience] : values.audiences,
    audienceDraft: "",
    isAddingAudience: false,
  };
};

/** Patch that abandons the adder without committing. */
export const cancelAudiencePatch = (): Pick<
  StoryDetailsCommon,
  "audienceDraft" | "isAddingAudience"
> => ({ audienceDraft: "", isAddingAudience: false });

/**
 * The adder's keyboard contract: Enter commits, Escape abandons, anything else
 * is not ours. `null` means "no patch" so the caller does not re-render on
 * every keystroke.
 */
export const audienceKeyPatch = (
  key: string,
  values: StoryDetailsCommon,
): AudiencePatch | ReturnType<typeof cancelAudiencePatch> | null => {
  if (key === "Enter") return commitAudiencePatch(values);
  if (key === "Escape") return cancelAudiencePatch();
  return null;
};
